import * as React from 'react';

import { TAdBannerEventPrefix } from '../../../types/events';
import { getEventQueue } from '../../../utils/eventQueue';

interface IUseAdBannerMicrofrontendEventsParams {
  placeId: string;
  isReady: boolean;
  onStub?: () => void;
  onError?: () => void;
  onClose?: () => void;
}

/**
 * Подписка на lifecycle-события баннера, ключуемые переданным `placeId`.
 *
 * Подписка выполняется в `useEffect`, гейтится флагом `isReady` (микрофронтенд смонтирован
 * только на клиенте) и снимается в cleanup. Топики строятся из того же `placeId`, что передаётся
 * в `<Microfrontend parameters={...}>`, поэтому хост не читает `instance.id`.
 */
export function useAdBannerMicrofrontendEvents({
  placeId,
  isReady,
  onStub,
  onError,
  onClose,
}: IUseAdBannerMicrofrontendEventsParams): void {
  React.useEffect(() => {
    if (!isReady || (!onStub && !onError && !onClose)) {
      return undefined;
    }

    const eventQueue = getEventQueue();
    const prefix: TAdBannerEventPrefix = `ad-banner-frontend-reporting.v1.${placeId}.ad-banner-`;
    const stubEventName = `${prefix}stub` as const;
    const errorEventName = `${prefix}error` as const;
    const closeEventName = `${prefix}close` as const;

    if (onStub) {
      eventQueue.on(stubEventName, onStub);
    }

    if (onError) {
      eventQueue.on(errorEventName, onError);
    }

    if (onClose) {
      eventQueue.on(closeEventName, onClose);
    }

    return () => {
      if (onStub) {
        eventQueue.off(stubEventName, onStub);
      }

      if (onError) {
        eventQueue.off(errorEventName, onError);
      }

      if (onClose) {
        eventQueue.off(closeEventName, onClose);
      }
    };
  }, [placeId, isReady, onStub, onError, onClose]);
}
